/**
 * EWM-1136: MSAL fetches the B2C metadata document over the network on every cold sign-in.
 * When that one request blips it throws ClientAuthError(endpoints_resolution_error), which the
 * shell surfaces as the terminal "sign-in failed" screen from EWM-1030 - indistinguishable, to
 * the user, from a rejected credential. 20 sign-ins were dropped that way on DEV and STAGE over
 * the 7 days to 2026-09-23, with all four B2C metadata endpoints serving 200 throughout.
 *
 * A transient network failure is worth exactly one automatic retry. A rejected credential is
 * worth none, which is why this matches on the error code rather than retrying every failure.
 */
const TRANSIENT_ERROR_CODES = [
  'endpoints_resolution_error',
  'openid_config_error',
  'network_error',
];

/**
 * The retry navigates to B2C and back, which remounts the SPA - so a React ref cannot bound it.
 * Without a store that survives the round-trip the retry re-arms on every return from B2C, which
 * is precisely the redirect loop EWM-1030 removed. sessionStorage is already this app's MSAL
 * cache location, so it is the store that shares the sign-in's lifetime.
 */
const RETRY_FLAG = 'estiworld.transientSignInRetried';

const errorCodeOf = (error: unknown): string =>
  typeof error === 'object' && error !== null && 'errorCode' in error
    ? String((error as { errorCode: unknown }).errorCode)
    : '';

export const isTransientAuthFailure = (error: unknown): boolean =>
  TRANSIENT_ERROR_CODES.includes(errorCodeOf(error));

/**
 * True at most once per browser session, and only for a transient failure. On true the caller
 * re-runs the sign-in; on false the shell shows the error screen and its manual retry button.
 */
export const claimTransientSignInRetry = (error: unknown): boolean => {
  if (!isTransientAuthFailure(error)) {
    return false;
  }

  try {
    if (sessionStorage.getItem(RETRY_FLAG) !== null) {
      return false;
    }
    sessionStorage.setItem(RETRY_FLAG, '1');
  } catch {
    // Storage blocked (private mode, blocked site data). Never retry blind: this flag is the
    // only thing between a failing sign-in and an unbounded redirect loop.
    return false;
  }

  return true;
};

/** Called on a successful sign-in so the next session gets its retry back. */
export const clearTransientSignInRetry = (): void => {
  try {
    sessionStorage.removeItem(RETRY_FLAG);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
};
